import React from "react";

// Mui Styles
import { StyledPaper } from "@/styles/mui/globalStyles";
import { questionnarieReportComparisonTheme } from "@/styles/mui/tableListTheme";
import { ThemeProvider } from "@mui/material/styles";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid2";
import Grow from "@mui/material/Grow";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

// CSS
import "@/styles/page/systemSettingProductPage.css";

// Navigate
import { useNavigate } from "react-router-dom";

// Components
import Layout from "@/components/layout";
import Title from "@/components/title";

// Fetch
import {
  getReplyQuestionnaireByUser,
  questionnaireSelection,
} from "@/fetch/fetchQuestionnarie";

// Moment
import Moment from "moment";
import { extendMoment } from "moment-range";
import "moment/locale/zh-hk";

const QuestionnaireComparisonPage = (props) => {
  const { open, setOpen } = props;
  const moment = extendMoment(Moment);

  // Navigate
  const navigate = useNavigate();

  // States
  const [optionList, setOptionList] = React.useState();
  const [snackSeverity, setSnackSeverity] = React.useState("success");
  const [snackMessage, setSnackMessage] = React.useState("");
  const [snackOpen, setSnackOpen] = React.useState(false);

  // Fetch
  const fetchAll = async () => {
    try {
      const { data: questSelectData } = await questionnaireSelection();
      if (questSelectData) {
        setOptionList(questSelectData);
      }
    } catch (err) {
      console.log(err);
    }
  };

  // Browser Back
  const handleBeforeUnload = (e) => {
    e.preventDefault();
    const message =
      "Are you sure you want to leave? All provided data will be lost.";
    e.returnValue = message;
    return message;
  };

  React.useEffect(() => {
    fetchAll();
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout
      header="StaffManagePage"
      snackOpen={snackOpen}
      snackClose={() => setSnackOpen(false)}
      snackMessage={snackMessage}
      snackSeverity={snackSeverity}
      open={open}
      setOpen={setOpen}
    >
      <Title
        title={`問卷表格比較`}
        history={() => navigate(-1)}
        children={<div></div>}
      />

      <StyledPaper page={"yes"}>
        <ThemeProvider theme={{}}>
          <Grid container>
            <Container>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  minHeight: 550,
                  gap: 5,
                }}
              >
                <CompareReport {...{ optionList }} />
                <CompareReport {...{ optionList }} />
              </Box>
            </Container>
          </Grid>
        </ThemeProvider>
      </StyledPaper>
    </Layout>
  );
};

export default QuestionnaireComparisonPage;

function CompareReport({ optionList = [] }) {
  const [data, setData] = React.useState();
  const [questionData, setQuestionData] = React.useState();

  const getQuestionData = async (promoter_id, questId) => {
    try {
      const { data: questionValue } = await getReplyQuestionnaireByUser(
        promoter_id,
        questId
      );
      if (questionValue) setQuestionData(questionValue);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <Box>
      <Stack
        sx={{ maxWidth: 500 }}
        direction={"row"}
        justifyContent={"flex-start"}
        alignItems={"center"}
        gap={2}
      >
        <Autocomplete
          value={data}
          options={optionList}
          getOptionKey={(option) => option.id}
          getOptionLabel={(option) => option.title}
          sx={{ width: 250 }}
          onChange={(_, data) => {
            setData(data);
          }}
          renderInput={(params) => <TextField {...params} label="標題" />}
        />
        <SplitButton
          {...{
            data: data?.promoter ?? [],
            getQuestionData: getQuestionData,
          }}
        />
      </Stack>
      <Report questionData={questionData} />
    </Box>
  );
}

function SplitButton(props) {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  return (
    <React.Fragment>
      <ButtonGroup
        variant="contained"
        ref={anchorRef}
        aria-label="Button group with a nested menu"
      >
        <Button
          size="small"
          aria-controls={open ? "split-button-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-label="select merge strategy"
          aria-haspopup="menu"
          onClick={handleToggle}
        >
          <Typography variant="button" noWrap>
            By user
          </Typography>
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        sx={{ zIndex: 1 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper sx={{ mt: 2 }}>
              <ClickAwayListener onClickAway={handleClose}>
                <Autocomplete
                  options={props?.data}
                  getOptionKey={(option) => option.promoter_id}
                  getOptionLabel={(option) =>
                    `${
                      option.promoter_alias +
                      "\u00a0".repeat(3) +
                      option.promoter_staff_code
                    }`
                  }
                  onChange={(_, data) =>
                    props?.getQuestionData(data.questId, data.promoter_id)
                  }
                  sx={{ width: 250 }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="員工" />
                  )}
                />
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  );
}

function Report({ questionData = { answer: [] } }) {
  return (
    <Stack
      divider={<Divider orientation="horizontal" flexItem />}
      sx={{
        minWidth: 500,
        border: questionData.answer?.length > 0 ? 1 : 0,
        borderRadius: 2,
        mt: 2,
      }}
    >
      <Typography align="center" variant="subtitle1">
        {questionData.alias}
      </Typography>
      {questionData.answer?.length > 0 &&
        questionData.answer.map((item, index) => {
          return (
            <List dense key={index}>
              <ListItem>
                <ThemeProvider theme={questionnarieReportComparisonTheme}>
                  <ListItemText
                    disableTypography
                    primary={
                      <Typography
                        component="span"
                        variant="body1"
                        sx={{
                          fontWeight: "700",
                          color: "text.primary",
                          display: "block",
                        }}
                      >
                        {item.question}
                      </Typography>
                    }
                    secondary={
                      item.type === "uploadFile" ? (
                        <ImageList rowHeight={"auto"}>
                          {item.answer.map((img, index) => (
                            <ImageListItem cols={2} key={index}>
                              <img src={img} loading="lazy" />
                            </ImageListItem>
                          ))}
                        </ImageList>
                      ) : Array.isArray(item.answer) ? (
                        item.answer
                          .filter((filterItem) => filterItem.value)
                          .map((item, index) => (
                            <Typography noWrap={false} component="span">
                              {`${index + 1}. ${item.label}`}
                              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                            </Typography>
                          ))
                      ) : (
                        <Typography component="span">{item.answer}</Typography>
                      )
                    }
                  />
                </ThemeProvider>
              </ListItem>
            </List>
          );
        })}
    </Stack>
  );
}
